import { defineComponent as _defineComponent } from 'vue'

import { hyphenatedCurrentLocale } from '@@/bits/current_locale'
import { __ } from '@@/bits/intl'
import { transformToSafeRedirectUrl } from '@@/bits/post_processing'
import { getCustomNumberFieldValueForDisplay, isWallCustomPostPropertyOfType } from '@@/bits/post_properties'
import { getCustomDateFieldStringForDisplay } from '@@/bits/time'
import OzContainedButton, {
  OzContainedButtonColorScheme,
  OzContainedButtonSizePreset,
} from '@@/library/v4/components/OzContainedButton.vue'
import { useSurfacePostPropertiesStore } from '@@/pinia/surface_post_properties'
import type { Post, PostCustomPropertyValue, WallCustomPostProperty } from '@@/types'
import { storeToRefs } from 'pinia'
import type { Ref, VNodeData } from 'vue'
import { computed, onMounted, ref } from 'vue'

interface PostPropertyDisplayAttributes {
  id: string
  name: string
  formattedValue: string
}

export type CustomPropertyTextRefs = Record<'customPropertyLabelRefs' | 'customPropertyValueRefs', Ref<HTMLElement[]>>
const __default__ = {}

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  __name: 'SurfacePostProperties',
  props: {
    darkMode: { type: Boolean },
    locationName: { default: undefined },
    wallCustomPostProperties: null,
    customPropertyValues: null,
    propertyLabelClass: { default: 'text-body-extra-small' },
    propertyValueClass: { default: 'text-body-small' },
    linkButtonSize: { default: 24 }
  },
  emits: ["dblclick", "update:text-element-refs"],
  setup(__props: any, { emit }: { emit: ({
  (name: 'dblclick', event: MouseEvent): void
  (name: 'update:text-element-refs', event: CustomPropertyTextRefs)
}), expose: any, slots: any, attrs: any }) {

const props = __props as { darkMode: boolean, locationName: string, wallCustomPostProperties: WallCustomPostProperty[], customPropertyValues: Post['custom_properties'], propertyLabelClass: string, propertyValueClass: string, linkButtonSize: 24 | 32 | 40 | 56 }





const { customLinkButtonProperty } = storeToRefs(useSurfacePostPropertiesStore())

// Values used for displaying custom post property values
const customPostProperties = computed<PostPropertyDisplayAttributes[]>(() => {
  const customPropertyValues = props.customPropertyValues
  if (customPropertyValues == null) return []
  return props.wallCustomPostProperties
    .filter((property) => property.id != null)
    .map((property) => {
      return {
        id: property.id!, // non-null assert: already filtered out cases where property.id is blank
        name: property.name,
        formattedValue: getFormattedValue({ property, value: customPropertyValues[property.id!] }),
      }
    })
    .filter(({ formattedValue }) => formattedValue !== '')
})

const getFormattedValue = (options: { property: WallCustomPostProperty; value: PostCustomPropertyValue }): string => {
  if (!options.value) return ''

  if (isWallCustomPostPropertyOfType(options.property, 'text')) {
    return options.value as string
  }

  if (isWallCustomPostPropertyOfType(options.property, 'date')) {
    return getCustomDateFieldStringForDisplay({
      datetime: options.value,
      showTime: options.property.show_time,
      dateDisplayFormat: options.property.date_display_format,
    })
  }

  if (isWallCustomPostPropertyOfType(options.property, 'number')) {
    return getCustomNumberFieldValueForDisplay({
      locale: hyphenatedCurrentLocale,
      propertySettings: options.property,
      value: options.value,
    })
  }

  return ''
}

// Full list of property values: location property + custom properties
const postProperties = computed<PostPropertyDisplayAttributes[]>(() => {
  const properties: PostPropertyDisplayAttributes[] = []
  if (props.locationName) {
    properties.push({
      id: 'post-property-location',
      name: __('Location'),
      formattedValue: props.locationName,
    })
  }
  properties.push(...customPostProperties.value)
  return properties
})

const customPropertyLabelRefs: Ref<HTMLElement[]> = ref([])
const customPropertyValueRefs: Ref<HTMLElement[]> = ref([])

const addLabelRef: VNodeData['ref'] = (e): void => {
  customPropertyLabelRefs.value.push(e as HTMLElement)
}

const addValueRef: VNodeData['ref'] = (e): void => {
  customPropertyValueRefs.value.push(e as HTMLElement)
}

// Link button-related logic
const linkButtonPropertyValue = computed<string | undefined>(() => {
  if (customLinkButtonProperty.value?.id == null || props.customPropertyValues == null) return undefined

  const linkButtonUrl = props.customPropertyValues[customLinkButtonProperty.value.id] as string | undefined

  if (linkButtonUrl == null) return undefined
  if (!linkButtonUrl.startsWith('http') && !linkButtonUrl.startsWith('mailto')) {
    return `https://${linkButtonUrl}`
  }
  return linkButtonUrl
})

const linkButtonSizePreset = computed(() => {
  switch (props.linkButtonSize) {
    case 24:
      return OzContainedButtonSizePreset.H24px
    case 32:
      return OzContainedButtonSizePreset.H32px
    case 40:
      return OzContainedButtonSizePreset.H40px
    case 56:
      return OzContainedButtonSizePreset.H56px
    default:
      return OzContainedButtonSizePreset.H24px
  }
})

onMounted(() => {
  // For slideshow resizing logic
  emit('update:text-element-refs', {
    customPropertyLabelRefs,
    customPropertyValueRefs,
  })
})

return { __sfc: true,props, emit, customLinkButtonProperty, customPostProperties, getFormattedValue, postProperties, customPropertyLabelRefs, customPropertyValueRefs, addLabelRef, addValueRef, linkButtonPropertyValue, linkButtonSizePreset, transformToSafeRedirectUrl, OzContainedButton, OzContainedButtonColorScheme }
}

})